import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { FaChevronLeft, FaRegHeart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Meta from '../components/Meta';
import Products from '../components/Products';
import { addToCart } from '../slices/cartSlice';
import { trackAddToCart } from '../utils/analytics';
import { useWishlistQuery } from '../slices/usersApiSlice';
import {
    useAddToNotifyListMutation,
    useGetNotifyStatusQuery,
    useRemoveFromNotifyListMutation,
} from '../slices/productsApiSlice';

const WishlistAction = ({ product }) => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.auth);
    const { cartItems } = useSelector((state) => state.cart);

    const hasSizes = product.sizeArray?.length > 0;
    const hasOptions = hasSizes || product.colourArray?.length > 0;
    const inStock = hasSizes
        ? product.sizeArray.some((size) => size.tshirtInStock > 0)
        : product.countInStock > 0;

    const { data: notifyStatus } = useGetNotifyStatusQuery(
        { productId: product._id, userId: userInfo?._id },
        { skip: inStock || product.isVisible === false }
    );
    const [addToNotifyList, { isLoading: isAdding }] = useAddToNotifyListMutation();
    const [removeFromNotifyList, { isLoading: isRemoving }] = useRemoveFromNotifyListMutation();

    if (product.isVisible === false) {
        return <p className="wishlist-note">No longer available</p>;
    }

    if (!inStock) {
        const onList = !!notifyStatus?.onList;
        const toggleNotify = async () => {
            try {
                if (onList) {
                    await removeFromNotifyList(product._id).unwrap();
                } else {
                    await addToNotifyList(product._id).unwrap();
                    toast.success(`We'll email you when it's back in stock`);
                }
            } catch (err) {
                toast.error(err?.data?.message || 'Something went wrong. Please try again.');
            }
        };
        return (
            <button
                type="button"
                className="wishlist-action wishlist-action--outline"
                onClick={toggleNotify}
                disabled={isAdding || isRemoving}
            >
                {onList ? 'Cancel Alert' : 'Notify Me'}
            </button>
        );
    }

    if (hasOptions) {
        return (
            <Link to={`/product/${product.slug || product._id}`} className="wishlist-action wishlist-action--outline">
                Choose Options
            </Link>
        );
    }

    const handleAdd = () => {
        const existing = cartItems.find((item) => item._id === product._id && !item.size);
        const qty = Math.min((existing?.qty || 0) + 1, product.countInStock);
        dispatch(addToCart({ ...product, qty, size: '', colour: '' }));
        trackAddToCart(product, 1);
        toast.success('Added to basket');
    };

    return (
        <button type="button" className="wishlist-action" onClick={handleAdd}>
            Add To Basket
        </button>
    );
};

const WishlistScreen = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const { data: wishlist, isLoading, error } = useWishlistQuery(userInfo?._id, { skip: !userInfo });

    return (
        <div className="account-page account-page--wide">
            <Meta title="My Wishlist" />
            <Link to="/profile" className="account-back"><FaChevronLeft aria-hidden="true" /> My Account</Link>
            <h1 className="account-title">
                My Wishlist{wishlist?.length > 0 && <span className="account-title__count"> ({wishlist.length})</span>}
            </h1>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">We couldn't load your wishlist. Please try again.</Message>
            ) : wishlist?.length ? (
                <Row>
                    {wishlist.map((product) => (
                        <Col key={product._id} xs={6} md={4} lg={3} className="d-flex">
                            <Products product={product} showDiscount={product.isClearance}>
                                <WishlistAction product={product} />
                            </Products>
                        </Col>
                    ))}
                </Row>
            ) : (
                <div className="account-empty">
                    <FaRegHeart aria-hidden="true" className="account-empty__icon" />
                    <h2>Your wishlist is empty</h2>
                    <p>Tap the heart on any product to save it here.</p>
                    <Link to="/search" className="wishlist-action account-empty__link">Browse Products</Link>
                </div>
            )}
        </div>
    );
};

export default WishlistScreen;
